import { PhotoIcon, StarIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAttachmentValidator from 'src/hooks/attachments/useAttachmentValidator';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums';
import { useChatContext } from 'src/components/Chat/Chat.hook';
import { tAttachment, tChatEvent } from 'src/components/Chat/Chat.types';
import MessageMenu from '../../../MessageBubble/components/MessageMenu/MessageMenu.component';
import ReactionButton from '../../../MessageBubble/components/Reactions/ReactionButton.component';
import ReactionPill from '../../../MessageBubble/components/Reactions/ReactionPill.component';
import { canViewReactionsOn } from '../../../MessageBubble/components/Reactions/reactionChannels';
import useEventReaction from '../../../MessageBubble/hooks/useEventReaction.hook';

export type tAlbumTileProps = {
  /** The photo's own message: its menu, reaction, star and selection act on it. */
  event: tChatEvent;
  /** The photo, once decrypted; undefined while it loads. */
  attachment?: tAttachment;
  /** 1-based place in the album, for its accessible name. */
  position: number;
  total: number;
  /** The "+N" over the last tile of a collapsed album; 0 when there is none. */
  overflow: number;
  onOpen: () => void;
  onShowAll: () => void;
};

/**
 * One photo of an album (INT-1227). Every photo is its own WhatsApp message,
 * so the tile keeps that message's menu, reaction, star and selection. The
 * "+N" tile stands for the photos past it, and clicking it shows them all.
 */
export default function AlbumTile(props: tAlbumTileProps) {
  const { event, attachment, position, total, overflow, onOpen, onShowAll } =
    props;
  const { t } = useTranslation('chat/chat');
  const { isResending, resendIds, selectMessageToReply, selectedChat } =
    useChatContext();
  const handleReaction = useEventReaction(event);
  const { isValid } = useAttachmentValidator(
    attachment?.url,
    attachment?.mediaType,
  );
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setLoadError(false);
  }, [attachment?.url]);

  const isWapiChannel = selectedChat?.integration === eChannelType.WHATSAPP_API;
  const canViewReactions = canViewReactionsOn(selectedChat?.integration);
  const isSelected = resendIds.includes(event.id);
  const reactions = event.reactions ?? [];
  const myReaction = reactions.find((reaction) => reaction.fromMe);
  const photoLabel = t('album.photo', { position, total });
  const isExpired = Boolean(attachment?.url) && (!isValid || loadError);

  return (
    <div
      className={classNames('AlbumTile group', {
        'AlbumTile--selected': isSelected,
      })}
      data-testid="album-tile"
    >
      <button
        type="button"
        className="AlbumTile__Open"
        onClick={overflow ? onShowAll : onOpen}
        aria-label={overflow ? t('album.showAll', { total }) : photoLabel}
      >
        {isExpired ? (
          <span className="AlbumTile__Expired" title={t('attachmentExpired')}>
            <PhotoIcon className="AlbumTile__Expired__Icon" aria-hidden="true" />
          </span>
        ) : attachment?.url ? (
          <img
            className="AlbumTile__Image"
            src={attachment.url}
            alt={photoLabel}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setLoadError(true)}
          />
        ) : (
          <span className="AlbumTile__Placeholder" aria-hidden="true" />
        )}
        {overflow > 0 && (
          <span className="AlbumTile__Overflow" aria-hidden="true">
            +{overflow}
          </span>
        )}
      </button>

      {isResending && (
        <input
          type="checkbox"
          className="AlbumTile__Check"
          checked={isSelected}
          onChange={() => selectMessageToReply(isSelected, event.id)}
          aria-label={t('album.select', { position, total })}
        />
      )}

      {!overflow && !isResending && (
        <div className="AlbumTile__Actions">
          {isWapiChannel && (
            <ReactionButton
              onSelect={handleReaction}
              currentEmoji={myReaction?.emoji}
            />
          )}
          <MessageMenu chatEvent={event} />
        </div>
      )}

      {event.message?.isFavorite && (
        <StarIcon className="AlbumTile__Star" aria-hidden="true" />
      )}

      {canViewReactions && reactions.length > 0 && (
        <div className="AlbumTile__Reactions">
          <ReactionPill
            reactions={reactions}
            fromMe={Boolean(event.message?.fromMe)}
            onToggleReaction={isWapiChannel ? handleReaction : undefined}
          />
        </div>
      )}
    </div>
  );
}
